import React, { FC, useState, useEffect, useRef } from 'react';
import { ExtendedGridColDef } from '@/types/ExtendedGridColDef';
import { Grid, Typography, Paper, Box, Chip, Divider } from '@mui/material';
import { SCUIButton, SCUINoRecordComponent, SCUILoadingLinear } from '../../atomic/atoms';
import { SCUIContent } from '../../atomic/organisms';
import { useTranslation } from 'react-i18next';
import { SCUITextFieldWithSelectBox } from '../../atomic/molecules';
import { SCUIBreadCrumb, SCUICustomDataGrid, SCUISelectFieldWithButton } from '../../atomic/molecules';
import { CargoQuery, RefundQuery, ShippmentQuery } from '../../services/query';
import TimeHelper from '../../utils/time';
import Helper from '../../utils/helper';
import { DowmloadExcelMPPrepareReturnShipment } from './components';
import { ChildFunctions } from './MPPrepareReturnToSellerList.config';
import { MPRTS } from './mpPrepareReturnToSellerList.classes';
import Swal from 'sweetalert2';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import SendIcon from '@mui/icons-material/Send';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ChecklistIcon from '@mui/icons-material/Checklist';
import {
    mapReturnToSellerRows,
    resolveSelectedReturnRows,
    validatePrepareReturnSelection,
} from './mpPrepareReturnToSellerList.helpers';
export const MPPrepareReturnToSellerList: FC<any> = () => {
    const [datas, setDatas] = React.useState<any>();
    const [datasFirst, setDatasFirst] = React.useState<any>();
    const [cargoShipmentCodeArray, setCargoShipmentCodeArray] = React.useState<any>();
    const [selectedRows, setSelectedRows] = useState<any[]>([]);
    const childRef = useRef<ChildFunctions | null>(null);
    const [loading, setLoading] = React.useState(false);
    const { t } = useTranslation();
    const [cargoNameList, setCargoNameList] = useState<any>();
    const [documentType, setDocumentType] = React.useState(1);
    const [selectedCargo, setSelectedCargo] = React.useState(0);
    const [isExcelExported, setIsExcelExported] = useState(false);
    const [selectedField, setSelectedField] = useState('');

    const handleExcelExport = () => {
        setIsExcelExported(true);
        childRef.current?.printSomething(false);
    };

    const GetCargoList = async () => {
        try {
            let cargoList: any = await CargoQuery.CompanyList();
            if (cargoList.data) {
                setCargoNameList(cargoList.data);
            }
        } catch (error) {
            console.log('Hata :>> ', error);
        }
    };
    const UpdateStoreToSellerShipmentStatusByCargoShipmentCode = async () => {
        const validation = validatePrepareReturnSelection(cargoShipmentCodeArray, isExcelExported);
        if (!validation.ok) {
            Swal.fire({
                icon: 'warning',
                title: validation.title,
                text: validation.text,
            });
            return;
        }
        childRef.current?.printSomething(true);
        await ShippmentQuery.UpdateStoreToSellerShipmentStatusByCargoShipmentCode({
            cargoShipmentCodeArray: cargoShipmentCodeArray,
        }).then((res: any) => {
            if (res) {
                GetReturnToSellerListShippingListsForStore();
            }
            Swal.fire('', '', 'success');
        });
    };
    const GetReturnToSellerListShippingListsForStore = () => {
        setLoading(true);
        RefundQuery.GetReturnToSellerListShippingListsForStoreV2({
            ShippingCompanyId: selectedCargo,
            StoreCode: Helper.getStoreCode() || 'T999',
            text: selectedField,
            documentType: documentType,
        }).then((res: any) => {
            const new_array = mapReturnToSellerRows(res.data.deliveryListShippingLists);
            setLoading(false);
            setDatas(new_array);
            setDatasFirst(new_array);
        });
    };

    useEffect(() => {
        GetCargoList();
    }, []);

    const columns: ExtendedGridColDef[] = [
        { field: 'id', flex: 1, hide: true },
        { field: 'shippingListNo', headerName: t('general.shippingNo'), flex: 1 },
        { field: 'orderNumber', headerName: t('general.orderNo'), flex: 1 },
        { field: 'cargoShipmentCode', headerName: t('general.customerReturnCode'), flex: 1 },
        {
            field: 'orderDate',
            headerName: t('general.orderDate'),
            flex: 1,
            renderCell: (params) => {
                return <div>{TimeHelper.formatDateHour(params.row.orderDate)}</div>;
            },
        },
        { field: 'sellerName', headerName: t('general.sellerName'), flex: 1 },
        { field: 'shippingCompanyName', headerName: t('general.cargoCompany'), flex: 1 },
    ];
    const handleSelectionModelChange = (model: any[]) => {
        setCargoShipmentCodeArray(model);
        setSelectedRows(resolveSelectedReturnRows(model, datasFirst));
    };

    return (
        <SCUIContent>
            <Box className={MPRTS.page} sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                {/* BreadCrumb — untouched */}
                <Grid className={MPRTS.breadcrumbSection} container direction="row">
                    <SCUIBreadCrumb
                        title={t('general.mpStoreReturnsShipmentScreen')}
                        componentLink={'MPPrepareReturnToSellerList'}
                    />
                </Grid>

                {/* Hidden excel ref component */}
                {selectedRows && (
                    <DowmloadExcelMPPrepareReturnShipment
                        data={selectedRows}
                        ref={childRef}
                        shippingCompanyId={selectedCargo}
                    />
                )}

                <div className={MPRTS.loadingSection}>
                    <SCUILoadingLinear display={loading} />
                </div>

                {/* Cargo company selector card */}
                <Paper
                    className={MPRTS.cargoCard}
                    elevation={0}
                    sx={{
                        border: '1px solid',
                        borderColor: 'grey.200',
                        borderRadius: 3,
                        px: 2.5,
                        py: 2,
                        boxShadow: '0 2px 12px rgba(0,0,0,0.05)',
                    }}
                >
                    <Box className={MPRTS.cargoCardHeader} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: '16px' }}>
                        <LocalShippingIcon sx={{ fontSize: 17, color: 'primary.main' }} />
                        <Typography fontWeight={700} fontSize={13} color="text.primary">
                            {t('cargoFeeManagement.selectCargoCompany')}
                        </Typography>
                    </Box>
                    <Box className={MPRTS.cargoSelectBlock}>
                        <SCUISelectFieldWithButton
                            selectPlaceHolder={t('cargoFeeManagement.selectCargoCompany')}
                            selectValue={selectedCargo}
                            selectId={'id'}
                            selectText={'name'}
                            onChangeSelect={(event: any) => {
                                setSelectedCargo(event.target.value as number);
                            }}
                            selectList={cargoNameList}
                            onClickButton={() => GetReturnToSellerListShippingListsForStore()}
                            onKeyPressTextField={(e: any) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    GetReturnToSellerListShippingListsForStore();
                                }
                            }}
                            buttonText={t('general.listShipments')}
                        />
                    </Box>
                </Paper>

                {datas ? (
                    <React.Fragment>
                        {/* Search by order/shipping no */}
                        <Box className={MPRTS.searchBlock}>
                            <SCUITextFieldWithSelectBox
                                placeholder={' '}
                                selectList={[
                                    { value: 1, text: t('general.orderNo') },
                                    { value: 2, text: t('general.shippingNo') },
                                ]}
                                onChangeSelect={(event: any) => {
                                    setDocumentType(event.target.value as number);
                                }}
                                selectValue={documentType}
                                onChangeTextField={(event: any) => {
                                    const inputValue = event.target.value.trim();
                                    setDocumentType(inputValue.startsWith('5') ? 2 : 1);
                                    setSelectedField(inputValue);
                                }}
                                onClickButton={() => GetReturnToSellerListShippingListsForStore()}
                                onKeyPressTextField={(e: any) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        setSelectedField(e.target.value);
                                    }
                                }}
                            />
                        </Box>

                        {/* DataGrid — untouched */}
                        <Box className={MPRTS.gridWrap}>
                            <SCUICustomDataGrid
                                selectionOnClick={true}
                                pageSize={50}
                                checkboxSelection
                                rows={datas}
                                columns={columns}
                                onSelectionModelChange={handleSelectionModelChange}
                                selectionModel={selectedRows.map((row) => row.id)}
                            />
                        </Box>

                        {/* Warning notices */}
                        <Paper
                            className={MPRTS.warningsPaper}
                            elevation={0}
                            sx={{
                                border: '1px solid',
                                borderColor: 'warning.light',
                                borderLeft: '4px solid',
                                borderLeftColor: 'warning.main',
                                borderRadius: 2,
                                px: 2.5,
                                py: 1.5,
                                backgroundColor: 'rgba(255,167,38,0.05)',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 1,
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <WarningAmberIcon sx={{ fontSize: 17, color: 'warning.main', flexShrink: 0 }} />
                                <Typography fontSize={13} fontWeight={600} color="warning.dark">
                                    Yazdırma işlemi için tarayıcıda pop-up izni açık olmalıdır!
                                </Typography>
                            </Box>
                            <Divider sx={{ borderColor: 'warning.light' }} />
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <WarningAmberIcon sx={{ fontSize: 17, color: 'warning.main', flexShrink: 0 }} />
                                <Typography fontSize={13} fontWeight={600} color="warning.dark">
                                    Yazdırma işlemi yapmadan sayfadan ayrılmayınız!
                                </Typography>
                            </Box>
                        </Paper>

                        {/* Order count + action buttons row */}
                        <Box
                            className={MPRTS.actionsRow}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                flexWrap: 'wrap',
                                gap: 2,
                                mb: 3,
                            }}
                        >
                            {selectedRows && (
                                <Chip
                                    className={MPRTS.selectedCountChip}
                                    icon={<ChecklistIcon />}
                                    label={`Toplam Sipariş Sayısı: ${selectedRows.length}`}
                                    size="medium"
                                    sx={{
                                        fontSize: 13,
                                        fontWeight: 700,
                                        height: 34,
                                        backgroundColor: selectedRows.length > 0 ? 'rgba(25,118,210,0.08)' : 'grey.100',
                                        color: selectedRows.length > 0 ? 'primary.main' : 'text.secondary',
                                        border: '1px solid',
                                        borderColor: selectedRows.length > 0 ? 'rgba(25,118,210,0.25)' : 'grey.300',
                                        '& .MuiChip-icon': {
                                            fontSize: 18,
                                            color: selectedRows.length > 0 ? 'primary.main' : 'grey.400',
                                        },
                                    }}
                                />
                            )}

                            <Box className={MPRTS.actionsButtonsWrap} sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                                <SCUIButton
                                    className={MPRTS.inputButtonExportExcel}
                                    type="primary"
                                    sizeType="fixed"
                                    text="Excel'e Aktar"
                                    size="s"
                                    showStartIcon
                                    startIcon={<FileDownloadIcon style={{ fontSize: '15px' }} />}
                                    style={{
                                        height: '36px',
                                        padding: '0 20px',
                                        borderRadius: '8px',
                                        fontWeight: 600,
                                        fontSize: '13px',
                                    }}
                                    disabled={cargoShipmentCodeArray?.length > 0 ? false : true}
                                    onClick={() => handleExcelExport()}
                                />
                                <SCUIButton
                                    className={MPRTS.inputButtonSendShipmentMail}
                                    type="primary"
                                    sizeType="fixed"
                                    text="Sevk Listesini Mail Olarak Gönder"
                                    size="s"
                                    showStartIcon
                                    startIcon={<SendIcon style={{ fontSize: '15px' }} />}
                                    style={{
                                        height: '36px',
                                        padding: '0 20px',
                                        borderRadius: '8px',
                                        fontWeight: 600,
                                        fontSize: '13px',
                                        backgroundColor: !isExcelExported ? 'rgba(224,224,224,1)' : undefined,
                                        pointerEvents: !isExcelExported ? 'auto' : undefined,
                                        border: !isExcelExported ? 'none' : undefined,
                                    }}
                                    onClick={() => UpdateStoreToSellerShipmentStatusByCargoShipmentCode()}
                                />
                            </Box>
                        </Box>
                    </React.Fragment>
                ) : (
                    <Box className={MPRTS.emptyStateWrap}>
                        <SCUINoRecordComponent />
                    </Box>
                )}
            </Box>
        </SCUIContent>
    );
};
